import {inject, Injectable, signal, computed} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from 'environments/environment';
import {map, Observable, tap} from 'rxjs';
import {
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  SuperAdminProfile,
} from '../models/auth.model';
import {ApiService} from '../services/api.service';

@Injectable({providedIn: 'root'})
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  private readonly currentAdmin = signal<SuperAdminProfile | null>(null);

  readonly admin = this.currentAdmin.asReadonly();
  readonly isLoggedIn = computed(() => !!this.currentAdmin() && !!this.getToken());
  readonly isSuperAdmin = computed(() => this.currentAdmin()?.role === 'SUPER_ADMIN');

  constructor() {
    this.loadStoredAdmin();
  }

  login(credentials: LoginRequest): Observable<LoginResponse['data']> {
    return this.api.post<LoginResponse['data']>('auth/login', credentials).pipe(
      map(res => res.data),
      tap(data => {
        this.saveSession(data.accessToken, data.admin);
      })
    );
  }

  logout(redirect = true): void {
    localStorage.removeItem(environment.tokenKey);
    localStorage.removeItem(environment.adminDataKey);
    this.currentAdmin.set(null);

    if (redirect) {
      this.router.navigate(['/auth/login']);
    }
  }

  getProfile(): Observable<SuperAdminProfile> {
    return this.api.get<SuperAdminProfile>('auth/me').pipe(
      map(res => res.data),
      tap(admin => {
        this.currentAdmin.set(admin);
        localStorage.setItem(environment.adminDataKey, JSON.stringify(admin));
      })
    );
  }

  changePassword(req: ChangePasswordRequest): Observable<void> {
    return this.api.put<void>('auth/password', req).pipe(map(() => void 0));
  }

  getToken(): string | null {
    return localStorage.getItem(environment.tokenKey);
  }

  private saveSession(token: string, admin: SuperAdminProfile): void {
    localStorage.setItem(environment.tokenKey, token);
    localStorage.setItem(environment.adminDataKey, JSON.stringify(admin));
    this.currentAdmin.set(admin);
  }

  private loadStoredAdmin(): void {
    const token = this.getToken();
    const storedAdmin = localStorage.getItem(environment.adminDataKey);

    if (!token || !storedAdmin) {
      return;
    }

    try {
      this.currentAdmin.set(JSON.parse(storedAdmin));
    } catch {
      this.logout(false);
    }
  }
}
